import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { COUNTRIES, DEFAULT_COUNTRY, flagUrl } from "@/lib/countries";

/**
 * Phone input with IP-based country detection + complete country list (245+).
 * Shows SVG flag + dial prefix on the left, number input on the right.
 */

function Flag({ iso, size = 18, rounded = true, className = "" }) {
  if (!iso) {
    return (
      <span
        className={`inline-block rounded-sm bg-[#E2E8F0] ${className}`}
        style={{ width: size * 1.4, height: size }}
        aria-hidden
      />
    );
  }
  return (
    <img
      src={flagUrl(iso)}
      alt=""
      width={size * 1.4}
      height={size}
      loading="lazy"
      decoding="async"
      className={`inline-block ${rounded ? "rounded-[2px]" : ""} object-cover shadow-[0_0_0_1px_rgba(15,23,42,0.06)] ${className}`}
      style={{ width: size * 1.4, height: size }}
    />
  );
}

export default function PhoneInput({ value, onChange, country, onCountryChange, placeholder = "Nr. Cel", testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  // Detect country via IP on mount (only if no country already chosen)
  useEffect(() => {
    let cancelled = false;
    if (country && country.iso) return;
    (async () => {
      try {
        const res = await fetch("https://ipwho.is/", { cache: "no-store" });
        const j = await res.json();
        if (cancelled || !j || !j.success) return;
        const iso = j.country_code;
        const dial = String(j.calling_code || "").replace("+", "");
        if (iso && dial) {
          const match = COUNTRIES.find((c) => c[1] === iso);
          onCountryChange?.({ name: match?.[0] || j.country, iso, dial });
        }
      } catch {
        /* silently fall back to default */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = country?.iso ? country : DEFAULT_COUNTRY;

  const filtered = useMemo(() => {
    if (!query) return COUNTRIES;
    const q = query.toLowerCase().replace("+", "");
    return COUNTRIES.filter(
      ([n, iso, dial]) =>
        n.toLowerCase().includes(q) || dial.includes(q) || iso.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-stretch rounded-md border border-line bg-white focus-within:border-[#FF7A00] focus-within:ring-2 focus-within:ring-[#FF7A00]/20">
        {/* Country selector trigger */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 rounded-l-md border-r border-line bg-[#FAFBFC] px-3 text-sm text-ink transition-colors hover:bg-[#F1F5F9]"
          aria-label="Zgjidh shtetin"
          data-testid={`${testid}-country`}
        >
          <Flag iso={current.iso} size={16} />
          <span className="font-mono text-[13px] font-semibold text-slate2">+{current.dial}</span>
          <ChevronDown size={13} className={`text-slate3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {/* Number input */}
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="flex-1 rounded-r-md bg-white px-3 py-2 text-sm text-ink outline-none placeholder:text-slate3/70"
          data-testid={testid}
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1.5 overflow-hidden rounded-md border border-line bg-white shadow-cinematic-light">
          <div className="border-b border-line p-2">
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate3" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Kërko shtetin ose prefiksin…"
                className="w-full rounded-md border border-line bg-white py-1.5 pl-8 pr-2.5 text-[13px] text-ink outline-none focus:border-[#FF7A00]"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-[13px] text-slate3">Asgjë nuk u gjet.</div>
            )}
            {filtered.map(([name, iso, dial]) => (
              <button
                type="button"
                key={iso}
                onClick={() => {
                  onCountryChange?.({ name, iso, dial });
                  setOpen(false);
                  setQuery("");
                }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] transition-colors hover:bg-[#FAFBFC] ${
                  current.iso === iso ? "bg-[#FFF7ED] text-ink" : "text-ink"
                }`}
              >
                <Flag iso={iso} size={14} />
                <span className="flex-1 truncate">{name}</span>
                <span className="font-mono text-[12px] text-slate3">+{dial}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
